import React, { useState, useRef } from "react";
import { generateDataGraph, generateProbability } from "./generators/generateProbability";
import { awardToken } from "../../awardtoken";

const CATEGORIES = [
  { key: "graph", label: "Data & Graphs" },
  { key: "prob", label: "Probability" }
];

const QUESTION_GENERATORS = {
  graph: generateDataGraph,
  prob: () => {
    const [text, answer] = generateProbability();
    return { text, answer };
  }
};
const SHEET_SIZES = [10, 25, 50];

// --- ILLUSTRATION COMPONENTS ---
function BarChart({ categories = [], values = [] }) {
  if (!categories || !values || !categories.length || !values.length) return null;
  const maxVal = Math.max(...values);
  return (
    <div style={{
      display: "flex", alignItems: "flex-end", justifyContent: "center",
      gap: 12, height: 100, marginTop: 8, marginBottom: 2,
    }}>
      {categories.map((cat, i) => (
        <div key={cat} style={{ textAlign: "center" }}>
          <div
            style={{
              background: "#20b8d9",
              width: 24,
              height: `${(values[i] / maxVal) * 80 + 12}px`,
              borderRadius: 4,
              marginBottom: 4,
              transition: "height 0.3s",
            }}
          />
          <div style={{ fontSize: 15 }}>{cat}</div>
        </div>
      ))}
    </div>
  );
}

function LineChart({ categories = [], values = [] }) {
  if (!categories || !values || !categories.length || !values.length) return null;
  const w = 38 * (categories.length - 1) + 40;
  const h = 100;
  const maxVal = Math.max(...values);
  const minVal = Math.min(...values);
  // Normalize y so higher is up
  const points = values.map((v, i) => [
    20 + i * 38,
    12 + ((maxVal - v) / (maxVal - minVal || 1)) * 76,
  ]);
  const line = points.map(p => p.join(",")).join(" ");
  return (
    <svg width={w} height={h} style={{ display: "block", margin: "12px auto" }}>
      {/* Grid lines */}
      <line x1="0" y1="90" x2={w} y2="90" stroke="#e7e7e7" strokeWidth="1" />
      {/* Data line */}
      <polyline
        points={line}
        fill="none"
        stroke="#19b2c7"
        strokeWidth="3"
        strokeLinejoin="round"
      />
      {/* Points */}
      {points.map(([x, y], i) => (
        <circle key={i} cx={x} cy={y} r={5} fill="#f7b12e" stroke="#19b2c7" strokeWidth="2" />
      ))}
      {/* Labels */}
      {categories.map((cat, i) => (
        <text key={cat}
          x={20 + i * 38} y={98} textAnchor="middle"
          fontSize="14" fill="#234"
        >{cat}</text>
      ))}
    </svg>
  );
}

function TableChart({ categories = [], values = [] }) {
  if (!categories || !values || !categories.length || !values.length) return null;
  return (
    <table style={{
      margin: "10px auto 0", borderCollapse: "collapse", fontSize: 15, minWidth: 130
    }}>
      <thead>
        <tr>
          {categories.map(cat =>
            <th key={cat}
              style={{ border: "1px solid #c1e5f6", background: "#eafcff", padding: "4px 8px" }}>
              {cat}
            </th>
          )}
        </tr>
      </thead>
      <tbody>
        <tr>
          {values.map((v, i) =>
            <td key={i}
              style={{ border: "1px solid #c1e5f6", background: "#fff", padding: "4px 8px" }}>
              {v}
            </td>
          )}
        </tr>
      </tbody>
    </table>
  );
}

function PieChart({ categories = [], percents = [] }) {
  if (!categories || !percents || !categories.length || !percents.length) return null;
  // Simple SVG pie chart for up to 6 slices
  let total = percents.reduce((a, b) => a + b, 0);
  let angles = percents.map(p => (p / total) * 360);
  let cur = 0;
  const colors = ["#20b8d9", "#f7b12e", "#ff79a1", "#93f28e", "#ffcf62", "#889cf4"];
  function describeArc(cx, cy, r, startAngle, endAngle) {
    const start = [
      cx + r * Math.cos((Math.PI / 180) * startAngle),
      cy + r * Math.sin((Math.PI / 180) * startAngle),
    ];
    const end = [
      cx + r * Math.cos((Math.PI / 180) * endAngle),
      cy + r * Math.sin((Math.PI / 180) * endAngle),
    ];
    const large = endAngle - startAngle > 180 ? 1 : 0;
    return [
      `M${cx},${cy}`,
      `L${start[0]},${start[1]}`,
      `A${r},${r} 0 ${large} 1 ${end[0]},${end[1]}`,
      "Z",
    ].join(" ");
  }
  let slices = [];
  for (let i = 0; i < percents.length; i++) {
    let start = cur;
    let end = cur + angles[i];
    slices.push(
      <path
        key={i}
        d={describeArc(50, 50, 40, start - 90, end - 90)}
        fill={colors[i % colors.length]}
        stroke="#fff"
        strokeWidth={1.5}
      />
    );
    cur = end;
  }
  // Legend
  return (
    <div style={{ margin: "14px 0" }}>
      <svg width={100} height={100} style={{ verticalAlign: "middle" }}>
        {slices}
      </svg>
      <div style={{ fontSize: 14, marginTop: 6, textAlign: "center" }}>
        {categories.map((cat, i) => (
          <span key={cat}
            style={{
              display: "inline-block",
              margin: "0 6px 4px 0",
              color: colors[i % colors.length]
            }}>
            <span style={{
              display: "inline-block",
              width: 12, height: 12,
              background: colors[i % colors.length],
              borderRadius: 2, marginRight: 3,
              border: "1.5px solid #eee"
            }} />
            {cat} ({percents[i]}%)
          </span>
        ))}
      </div>
    </div>
  );
}

// ---

export default function MathDataProbPractice({ onAwardTokens }) {
  const [category, setCategory] = useState("graph");
  const [practice, setPractice] = useState(true);
  const [question, setQuestion] = useState(generateDataGraph());
  const [userAnswer, setUserAnswer] = useState("");
  const [feedback, setFeedback] = useState("");
  const [score, setScore] = useState(0);
  const hasAwardedToken = useRef(false);

  // Worksheet print mode
  const [sheet, setSheet] = useState([]);
  const [showAnswers, setShowAnswers] = useState(false);

  function renderIllustration(q) {
    if (!q) return null;
    if (q.type === "bar")
      return <BarChart categories={q.categories} values={q.values} />;
    if (q.type === "line")
      return <LineChart categories={q.categories} values={q.values} />;
    if (q.type === "table")
      return <TableChart categories={q.categories} values={q.values} />;
    if (q.type === "pie" && q.percents)
      return <PieChart categories={q.categories} percents={q.percents} />;
    return null;
  }

  function nextQuestion() {
    setUserAnswer("");
    setFeedback("");
    setQuestion(QUESTION_GENERATORS[category]());
  }

  function checkAnswer() {
    const correct =
      String(userAnswer).trim().toLowerCase() ===
      String(question.answer).trim().toLowerCase();
    if (correct) {
      setFeedback("✅ Correct!");
      setScore((s) => {
        const nextScore = s + 1;
        if (nextScore === 10 && !hasAwardedToken.current) {
          if (onAwardTokens) onAwardTokens(1);
          awardToken(1);
          hasAwardedToken.current = true;
          setTimeout(() => {
            setFeedback("🎉 10 correct! You've earned a token!");
          }, 400);
        }
        return nextScore;
      });
    } else {
      setFeedback(`❌ Incorrect. The answer is ${question.answer}`);
    }
    setTimeout(nextQuestion, 1200);
  }

  function makeSheet(n) {
    setSheet(Array.from({ length: n }, () => QUESTION_GENERATORS[category]()));
    setShowAnswers(false);
    setPractice(false);
  }

  function backToPractice() {
    setSheet([]);
    setPractice(true);
    setFeedback("");
    setUserAnswer("");
    setScore(0);
    hasAwardedToken.current = false;
    setQuestion(QUESTION_GENERATORS[category]());
  }

  function printSheet() {
    window.print();
  }

  return (
    <div className="practice-container worksheet">
      <h2>Data & Probability Practice</h2>
      <div className="control-row no-print">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.key}
            className={category === cat.key ? "selected" : ""}
            onClick={() => {
              setCategory(cat.key);
              setSheet([]);
              setPractice(true);
              setScore(0);
              setFeedback("");
              setUserAnswer("");
              setQuestion(QUESTION_GENERATORS[cat.key]());
              hasAwardedToken.current = false;
            }}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Sheet/Worksheet Print Mode */}
      {!practice && sheet.length > 0 && (
        <>
          <div className="worksheets-title">
            {CATEGORIES.find((c) => c.key === category).label} Worksheet
          </div>
          <div className="worksheet-buttons no-print">
            {SHEET_SIZES.map((n) => (
              <button key={n} onClick={() => makeSheet(n)}>
                {n} Qs
              </button>
            ))}
            <button onClick={printSheet}>Print</button>
            <button onClick={() => setShowAnswers((v) => !v)}>
              {showAnswers ? "Hide Answers" : "Show Answers"}
            </button>
            <button onClick={backToPractice}>Back to Practice</button>
          </div>
          <div className="sheet-grid">
            {sheet.map((q, i) => (
              <div key={i}>
                <span style={{ fontWeight: 600 }}>{i + 1}. </span>
                {renderIllustration(q)}
                <div style={{ marginTop: 10 }}>{q.text}</div>
                <div className="math-underline" />
                {showAnswers && (
                  <div style={{ fontSize: "0.93em", color: "#2c8c3f", fontWeight: 600 }}>
                    Ans: {q.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}

      {/* Interactive Practice Mode */}
      {practice && (
        <>
          <div className="worksheet-buttons no-print">
            <span style={{ marginRight: 7 }}>Worksheet:</span>
            {SHEET_SIZES.map((n) => (
              <button key={n} onClick={() => makeSheet(n)}>
                {n} Qs
              </button>
            ))}
          </div>
          <div className="current-question">
            <strong>Q:</strong>{" "}
            {renderIllustration(question)}
            <div style={{ marginTop: 12 }}>{question.text}</div>
          </div>
          <form
            className="answer-form"
            onSubmit={(e) => {
              e.preventDefault();
              checkAnswer();
            }}
          >
            <input
              type="text"
              value={userAnswer}
              onChange={(e) => setUserAnswer(e.target.value)}
              placeholder="Your answer"
              autoFocus
            />
            <button type="submit">Submit</button>
          </form>
          <div style={{ minHeight: 28, marginBottom: 10 }}>{feedback}</div>
          <div className="score-text">Score: {score}</div>
        </>
      )}
    </div>
  );
}
